import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import type { MedioDePagoItem } from '@/shared/api/controllers/mediosDePago';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { trasladarMovimientoCaja, type MovimientoRow } from '../api';
import { CajaNombre } from './CajaNombre';
import { CajaSelect } from './CajaSelect';

/**
 * Pasar un Ingreso/Egreso manual a otra caja de la misma moneda (pedido
 * 2026-10: antes había que anularlo y cargarlo de nuevo). Sólo cambia la
 * caja; el server revalida todo y rechaza con mensaje lo que no corresponde
 * (impuestos asociados, caja destino con impuestos automáticos, saldo).
 */
export function TrasladarCajaDialog({
  row,
  cajaOrigen,
  moneda,
  destinos,
  sugerida,
  onClose,
}: {
  row: MovimientoRow;
  cajaOrigen: string;
  moneda: 'ARS' | 'MXN' | 'USD';
  /** Cajas de la misma moneda donde el usuario puede escribir (sin la de origen). */
  destinos: MedioDePagoItem[];
  /** La caja par (Efectivo Pesos ↔ Efectivo Pesos N), si existe: el caso típico. */
  sugerida?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [destino, setDestino] = useState(sugerida ?? '');
  const f = row.fields;

  const mut = useMutation({
    mutationFn: () => trasladarMovimientoCaja(row.id, destino),
    onSuccess: (r) => {
      if (r.outcome === 'updated') {
        toast.success('Movimiento trasladado', {
          description: `${etiquetaCaja(cajaOrigen)} → ${etiquetaCaja(destino)}`,
        });
        qc.invalidateQueries({ queryKey: ['tesoreria'] });
        onClose();
        return;
      }
      toast.error('No se pudo trasladar', { description: 'message' in r ? r.message : undefined });
    },
    onError: (err) => {
      toast.error('No se pudo trasladar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md" processing={mut.isPending && 'Trasladando…'}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700">
              <ArrowRightLeft className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Pasar a otra caja</DialogTitle>
              <DialogDescription>
                Sólo cambia la caja: el importe, la fecha y la cuenta contable quedan igual.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="rounded-lg border bg-secondary px-3 py-2 text-sm">
            <div className="mb-1 truncate font-medium text-foreground" title={f.Descripcion_MC ?? ''}>
              {f.Descripcion_MC || f.CatDesc_MC || '—'}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">{f.TipoMovimiento_MC === 'Egreso' ? 'Gasto' : f.TipoMovimiento_MC}</span>
              <span className="font-medium text-foreground">{f.Fecha_MC || '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Importe</span>
              <span className="font-medium tabular-nums text-foreground">
                {formatMoney(parseMoney(f.MontoNeto_MC) ?? 0, moneda)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Caja actual</span>
              <CajaNombre caja={cajaOrigen} className="font-medium text-foreground" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-sm">Caja destino</Label>
            {destinos.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay otra caja de la misma moneda donde puedas registrar movimientos.
              </p>
            ) : (
              <CajaSelect
                value={destino}
                onChange={setDestino}
                medios={destinos}
                loading={false}
                className="sm:w-full"
              />
            )}
            <p className="text-xs text-muted-foreground">
              Las cajas bancarias que generan impuestos automáticos no admiten traslado: ahí
              hay que anular y cargar de nuevo.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !destino}
            className="gap-2 bg-violet-600 text-white hover:bg-violet-700"
          >
            <ArrowRightLeft className="h-4 w-4" aria-hidden />
            Trasladar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
